import {useForwardSelection} from './useForwardSelection';
import {useEffect, useRef, useState} from 'react';
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap';
import {forwardMessage, getForwardTargets, listConversationFeed, listMessages} from '../../service/ChatService';
import {chatErrorMessage, conversationDisplayName} from './chatUtils';

const MAX_FORWARD_TARGETS = 10;
const TARGET_LABELS = {
    CURRENT_WORKER: 'Worker · Admin',
    TODAY_WORKER: "Today's assigned worker · Admin",
    PROPERTY: 'Property · Agency',
};
const TARGET_NOTICES = {
    PROPERTY_UNKNOWN: 'The property could not be identified. Search for an address or conversation.',
    PROPERTY_CHANNEL_UNAVAILABLE: 'The related property channel is unavailable. Search for another conversation.',
    WORKER_CHANNEL_UNAVAILABLE: 'A related worker channel is unavailable. Search for another conversation.',
};

export function mergeForwardResults(current, incoming) {
    const byConversation = new Map((current || []).map(item => [item.conversation_id, item]));
    for (const item of incoming || []) byConversation.set(item.conversation_id, item);
    return [...byConversation.values()];
}

export default function ForwardMessageModal(props) {
    return props.message ? <ForwardMessageDialog key={props.message.id} {...props}/> : null;
}

function ForwardMessageDialog({message, currentUserId, onHide, onForwarded}) {
    const [conversations, setConversations] = useState([]);
    const [suggestions, setSuggestions] = useState({property: null, targets: [], notices: []});
    const [suggestionsLoading, setSuggestionsLoading] = useState(true);
    const [suggestionsError, setSuggestionsError] = useState(null);
    const [selected, setSelected] = useState(() => new Map());
    const [clientIds, setClientIds] = useState({});
    const [query, setQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextCursor, setNextCursor] = useState('');
    const [hasMore, setHasMore] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);
    const [lastAttempt, setLastAttempt] = useState(null);
    const searchTerm = query.trim();
    const loading = searchTerm ? searchLoading : suggestionsLoading;
    const targetId=selected.size===1?[...selected.keys()][0]:undefined;
    const selection=useForwardSelection(message,targetId,()=>{setClientIds({});setResults([])});
    const [roots,setRoots]=useState([]),[rootBefore,setRootBefore]=useState(0),[rootLoading,setRootLoading]=useState(false);
    const targetIsChannel = selected.get(targetId)?.conversation.kind === 'CHANNEL';
    const threadRoots = roots.filter(row => targetIsChannel || row.reply_count > 0);
    useEffect(()=>{setRootBefore(0);setRoots([])},[targetId]);
    useEffect(()=>{if(!targetId)return;const controller=new AbortController();setRootLoading(true);void listMessages(targetId,{beforeSequence:rootBefore||undefined,signal:controller.signal}).then(result=>{if(!controller.signal.aborted)setRoots((result.messages||[]).filter(row=>!row.deleted_at))}).catch(cause=>{if(!controller.signal.aborted)setError(cause)}).finally(()=>{if(!controller.signal.aborted)setRootLoading(false)});return()=>controller.abort()},[targetId,rootBefore]);
    const requestGenerationRef = useRef(0);
    const pageControllerRef = useRef(null);

    useEffect(() => () => pageControllerRef.current?.abort(), []);

    useEffect(() => {
        const controller = new AbortController();
        getForwardTargets(message.id, controller.signal)
            .then(payload => {
                if (!controller.signal.aborted) setSuggestions(payload);
            })
            .catch(cause => {
                if (!controller.signal.aborted) setSuggestionsError(cause);
            })
            .finally(() => {
                if (!controller.signal.aborted) setSuggestionsLoading(false);
            });
        return () => controller.abort();
    }, [message.id]);

    useEffect(() => {
        const generation = ++requestGenerationRef.current;
        pageControllerRef.current?.abort();
        setConversations([]);
        setNextCursor('');
        setHasMore(false);
        setLoadingMore(false);
        setError(null);
        setSearchLoading(Boolean(searchTerm));
        if (!searchTerm) return undefined;
        const controller = new AbortController();
        pageControllerRef.current = controller;
        const timer = window.setTimeout(() => {
            listConversationFeed({query: searchTerm, signal: controller.signal})
            .then(payload => {
                if (controller.signal.aborted || generation !== requestGenerationRef.current) return;
                setConversations((payload.conversations || []).filter(item => !item.archived_at));
                setNextCursor(payload.next_cursor || '');
                setHasMore(Boolean(payload.has_more));
            })
            .catch(loadError => {
                if (!controller.signal.aborted && generation === requestGenerationRef.current) setError(loadError);
            })
            .finally(() => {
                if (!controller.signal.aborted && generation === requestGenerationRef.current) setSearchLoading(false);
            });
        }, 200);
        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [searchTerm]);

    async function loadMore() {
        if (!searchTerm || loading || !hasMore || !nextCursor || loadingMore || submitting) return;
        const generation = requestGenerationRef.current;
        pageControllerRef.current?.abort();
        const controller = new AbortController();
        pageControllerRef.current = controller;
        setLoadingMore(true);
        setError(null);
        try {
            const payload = await listConversationFeed({
                query: searchTerm,
                cursor: nextCursor,
                signal: controller.signal,
            });
            if (controller.signal.aborted || generation !== requestGenerationRef.current) return;
            setConversations(current => {
                const byId = new Map(current.map(item => [item.id, item]));
                for (const item of payload.conversations || []) {
                    if (!item.archived_at) byId.set(item.id, item);
                }
                return [...byId.values()];
            });
            setNextCursor(payload.next_cursor || '');
            setHasMore(Boolean(payload.has_more));
        } catch (loadError) {
            if (!controller.signal.aborted && generation === requestGenerationRef.current) setError(loadError);
        } finally {
            if (!controller.signal.aborted && generation === requestGenerationRef.current) setLoadingMore(false);
        }
    }

    function toggle(target) {
        const conversationId = target.conversation.id;
        setSelected(current => {
            const next = new Map(current);
            if (next.has(conversationId)) next.delete(conversationId);
            else if (next.size < MAX_FORWARD_TARGETS) next.set(conversationId, target);
            return next;
        });
    }

    async function submit() {
        if (!message || selected.size === 0 || submitting) return;
        setSubmitting(true);
        setError(null);
        const nextClientIds = {...clientIds};
        const targets = [...selected.keys()].map(conversationId => {
            nextClientIds[conversationId] ||= crypto.randomUUID();
            return {conversation_id: conversationId, client_message_id: nextClientIds[conversationId], ...selection.extras};
        });
        setClientIds(nextClientIds);
        try {
            const payload = await forwardMessage(message.id, targets);
            const nextResults = payload.results || [];
            setResults(current => mergeForwardResults(current, nextResults));
            const failedIds = nextResults.filter(item => item.status === 'FAILED').map(item => item.conversation_id);
            const successfulIds = nextResults.filter(item => item.status !== 'FAILED').map(item => item.conversation_id);
            setLastAttempt({successful: successfulIds.length, failed: failedIds.length});
            setSelected(current => new Map([...current].filter(([id]) => failedIds.includes(id))));
            if (successfulIds.length) await onForwarded?.(successfulIds);
        } catch (forwardError) {
            setError(forwardError);
        } finally {
            setSubmitting(false);
        }
    }

    const successCount = lastAttempt?.successful || 0;
    const failedCount = lastAttempt?.failed || 0;
    const visibleFailures = results.filter(item => item.status === 'FAILED');
    const availableTargets = loading ? [] : searchTerm
        ? conversations.map(conversation => ({conversation}))
        : suggestions.targets;
    const availableIds = new Set(availableTargets.map(target => target.conversation.id));
    const visibleTargets = [
        ...[...selected.values()].filter(target => !availableIds.has(target.conversation.id)),
        ...availableTargets,
    ];

    return (
        <Modal show={Boolean(message)} onHide={submitting ? undefined : onHide} centered scrollable aria-labelledby="chat-forward-title">
            <Modal.Header closeButton={!submitting}>
                <Modal.Title id="chat-forward-title">Forward message</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {(message?.attachments||[]).map(attachment=><Form.Check key={attachment.id} id={`forward-photo-${attachment.id}`} label={attachment.original_filename} checked={selection.attachments.includes(attachment.id)} disabled={submitting} onChange={()=>selection.toggleAttachment(attachment.id)}/>)}
                <Form.Control as="textarea" aria-label="Explanation (optional)" placeholder="Explanation (optional)" value={selection.comment} disabled={submitting} maxLength={10000} onChange={event=>selection.changeComment(event.target.value)}/>
                {targetId?<><Form.Label>Destination thread</Form.Label><Form.Select aria-label="Destination thread" disabled={submitting||rootLoading} value={selection.rootId} onChange={event=>selection.changeRoot(Number(event.target.value))}><option value={0}>{targetIsChannel ? 'Create a new thread' : 'Send to conversation'}</option>{threadRoots.map(row=><option key={row.id} value={row.id}>{(row.body||'Photos or attachment').slice(0,100)}</option>)}</Form.Select><Button disabled={rootLoading||roots.length<50} onClick={()=>setRootBefore(Math.min(...roots.map(row=>row.sequence)))}>Older threads</Button></>:null}

                <Form.Control
                    type="search"
                    value={query}
                    placeholder="Search workers, addresses or conversations"
                    aria-label="Search forward conversations"
                    disabled={submitting}
                    onChange={event => setQuery(event.target.value)}
                />
                {!searchTerm && suggestions.property ? <p className="text-muted mt-2 mb-0">Related to {suggestions.property.full_address}</p> : null}
                {!searchTerm && !suggestionsLoading ? suggestions.notices.map(notice => (
                    TARGET_NOTICES[notice] ? <p key={notice} className="text-muted mt-2 mb-0">{TARGET_NOTICES[notice]}</p> : null
                )) : null}
                <div className="chat-forward-selection" role="group" aria-label="Forward targets">
                    {loading ? <div className="chat-loading"><Spinner /></div> : null}
                    {visibleTargets.map(target => {
                        const {conversation} = target;
                        const checked = selected.has(conversation.id);
                        const reason = target.reason || suggestions.targets.find(item => item.conversation.id === conversation.id)?.reason;
                        return (
                            <label key={conversation.id} className={checked ? 'is-selected' : ''}>
                                <Form.Check
                                    type="checkbox"
                                    checked={checked}
                                    disabled={submitting || (!checked && selected.size >= MAX_FORWARD_TARGETS)}
                                    onChange={() => toggle(target)}
                                    aria-label={`Forward to ${conversationDisplayName(conversation, currentUserId)}`}
                                />
                                <span>
                                    {conversationDisplayName(conversation, currentUserId)}
                                    {TARGET_LABELS[reason] ? <small className="d-block text-muted">{TARGET_LABELS[reason]}</small> : null}
                                </span>
                            </label>
                        );
                    })}
                    {!loading && visibleTargets.length === 0 ? <p className="text-muted">{searchTerm ? 'No conversations found.' : 'Search to find another conversation.'}</p> : null}
                </div>
                {searchTerm && !loading && hasMore ? (
                    <Button variant="outline-secondary" size="sm" onClick={() => void loadMore()} disabled={loadingMore || submitting}>
                        {loadingMore ? <Spinner size="sm" /> : 'Load more conversations'}
                    </Button>
                ) : null}
                <div className="chat-forward-limit">{selected.size}/{MAX_FORWARD_TARGETS} selected</div>
                {results.length ? (
                    <Alert variant={failedCount ? 'warning' : 'success'}>
                        {successCount} forwarded{failedCount ? `, ${failedCount} failed. Retry sends only failed targets.` : '.'}
                    </Alert>
                ) : null}
                {visibleFailures.length ? (
                    <ul className="chat-forward-errors">
                        {visibleFailures.map(item => (
                            <li key={item.conversation_id}>{item.error?.message || 'Forward failed'}</li>
                        ))}
                    </ul>
                ) : null}
                {error ? <Alert variant="danger">{chatErrorMessage(error)}</Alert> : null}
                {!searchTerm && suggestionsError ? <Alert variant="danger">{chatErrorMessage(suggestionsError)} Search for a conversation to continue.</Alert> : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Close</Button>
                <Button onClick={submit} disabled={submitting || loading || selected.size === 0}>
                    {submitting ? <Spinner size="sm" /> : failedCount ? 'Retry failed' : 'Forward'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
